import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MenuProps } from 'antd';
import { Dropdown, Modal } from 'antd';
import { CalendarOutlined, DeleteOutlined, ExclamationCircleOutlined, MoreOutlined } from '@ant-design/icons';
import { useDeleteWhTaskItems } from 'api/warehouse-task/use-delete-wh-task-items';
import { useSetWhTaskItemPickUpDate } from 'api/warehouse-task/use-set-wh-task-item-pick-up-date';
import { UserRole, withRoleGuard } from 'shared/hocs/with-role-guard.tsx';
import { useRoleGuard } from 'shared/hooks/use-role-guard.ts';
import type { TDropdownChangeInfo } from 'types/dropdown-change-info';
import type { IWhTaskItemControlProps } from './types';

export const WhTaskItemControlComponent = (props: IWhTaskItemControlProps) => {
  const { whTaskItem } = props;
  const { t } = useTranslation();
  const allowDeleteItem = useRoleGuard([UserRole.admin, UserRole.assistant, UserRole.customer_manager]);

  const { mutateAsync: deleteWhTaskItem } = useDeleteWhTaskItems();
  const { mutateAsync: setWhTaskItemPickUpDate } = useSetWhTaskItemPickUpDate(whTaskItem.warehouseTaskId);

  const [modal, contextHolder] = Modal.useModal();
  const [isActiveControl, setIsActiveControl] = useState(false);

  const deactivateWithTimeout = () => {
    setTimeout(() => setIsActiveControl(false), 300);
  };

  const confirmDelete = () => {
    modal.confirm({
      title: t('common:confirm'),
      icon: <ExclamationCircleOutlined />,
      content: t('common:delete_confirm_text'),
      okText: t('common:delete'),
      cancelText: t('common:cancel'),
      onCancel: () => deactivateWithTimeout(),
      onOk: async () => {
        await deleteWhTaskItem({ whTaskId: whTaskItem.warehouseTaskId, whTaskItemIds: [whTaskItem.id] });
        deactivateWithTimeout();
      },
    });
  };

  const confirmTextKey = whTaskItem.pickedUpDate
    ? 'wh-task:delete_pick_up_status_confirm_text'
    : 'wh-task:mark_as_picked_up_confirm_text';

  const confirmSetPickUpDate = () => {
    modal.confirm({
      title: t('common:confirm'),
      icon: <ExclamationCircleOutlined />,
      content: t(confirmTextKey),
      okText: t('common:confirm'),
      cancelText: t('common:cancel'),
      onCancel: () => deactivateWithTimeout(),
      onOk: async () => {
        await setWhTaskItemPickUpDate({
          warehouseTaskItemIds: [whTaskItem.id],
          isPickedUp: !whTaskItem.pickedUpDate,
        });
        deactivateWithTimeout();
      },
    });
  };

  const handleDropdownClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'delete') {
      confirmDelete();
    } else if (key === 'set_pick_up_date') {
      confirmSetPickUpDate();
    }
  };

  const handleDropdownOpenChange = (value: boolean, info: TDropdownChangeInfo) => {
    if (info.source === 'trigger') {
      setIsActiveControl(value);
    }
  };

  return (
    <>
      <Dropdown
        key='more'
        trigger={['click']}
        menu={{
          items: [
            {
              key: 'set_pick_up_date',
              label: whTaskItem.pickedUpDate ? t('wh-task:delete_pick_up_status') : t('wh-task:set_pick_up_status'),
              icon: <CalendarOutlined />,
            },
            ...(allowDeleteItem
              ? [
                  {
                    key: 'delete',
                    label: t('common:delete'),
                    icon: <DeleteOutlined />,
                  },
                ]
              : []),
          ],
          onClick: handleDropdownClick,
        }}
        placement='bottomLeft'
        onOpenChange={handleDropdownOpenChange}>
        <MoreOutlined style={{ fontSize: 20 }} />
      </Dropdown>
      {isActiveControl ? contextHolder : null}
    </>
  );
};

export const WhTaskItemControl = withRoleGuard(WhTaskItemControlComponent, [
  UserRole.admin,
  UserRole.assistant,
  UserRole.customer_manager,
  UserRole.storekeeper,
]);
